<template>
    <mini-calculator-page>
        <div slot="summary-panel" class="flex-grid--vertical">
            <div class="p-animated-calc__results a-form-inputs u-full-width u-margin-auto">
                <mini-calculator-header title="Mesothelioma PSLA Calculator" icon="mesothelioma-psla-calculator"/>
                <div>
                    <control for="date_of_birth" label="Date of Birth">
                        <date-input ref="dateOfBirth"
                                    v-model="dateOfBirth"
                                    @input="handler"
                                    v-field-control:date_of_birth.required="{type:'date'}"
                        ></date-input>
                        <control-error :show="$error('date_of_birth')">
                            The date of birth is required and must be in the format DD/MM/YYYY
                        </control-error>
                    </control>
                    <control for="age_of_person" label="Age">
                        <input autocomplete="off"
                               v-model.number="ageOfPerson"
                               @input="handler"
                               ref="ageOfPerson"
                               type="number">
                        <control-error :show="$error('age')">
                            The age is required
                        </control-error>
                    </control>
                    <control for="duration_of_symptoms" label="Duration Of Symptoms"  before-after="months">
                        <input autocomplete="off"
                               v-model.number="durationOfSymptoms"
                               @input="handler"
                               ref="durationOfSymptoms"
                               type="number">
                        <control-error :show="$error('duration_of_symptoms')">
                            The duration of symptoms is required
                        </control-error>
                    </control>
                </div>

                <h2 class="u-r-margin-top-40">
                    Result
                </h2>

                <control for="result" label="PSLA Award" before-after="pound-on-dark">
                    <input autocomplete="off" disabled="disabled"
                           id="age_at_death" type="number"
                           name="age_at_death"
                           v-two-dp
                           v-model="pslaAward">
                </control>
            </div>
        </div>

        <div slot="results-panel" class="flex-grid--vertical" v-show="showResults">
            <div class="p-animated-calc__results-panel u-full-width u-margin-auto">
                <h2>Explanation</h2>

                <p>
                    Welcome to our Mesothelioma General Damages (PSLA) calculator. The calculator shows estimated common law awards
                    according to 'age' and 'duration of symptoms' &mdash; the period between onset of the disease
                    and death (or anticipated date of death).
                </p>

                <p>
                    There are two parts to the calculator:
                </p>

                <ol>
                    <li>
                        The first is a Tariff Table designed to give a broad brush indication of possible awards and
                        is not intended to substitute a detailed review and consideration of actual awards to
                        properly value any PSLA award. The Tariff Table is based on the 14th Edition of the JC Guidelines.
                    </li>
                    <li>
                        The second shows actual PLSA awards which most closely fit the selected age and duration
                        of symptoms.
                    </li>
                </ol>

                <div class="u-d-flex u-flex-space-between">
                    <h2>Tariff Table</h2>

                    <button class="a-button a-button--smaller" @click="applyUplift()">
                        {{ upliftButton ? 'Add' : 'Remove' }} 10% Uplift
                    </button>
                </div>

                <div class="a-table--short-no-style">
                    <table class="a-table a-table--full-width a-table--condensed" v-if="table && !loading.table">
                        <tr>
                            <th rowspan="2">Age (Up to)</th>
                            <th :colspan="table[50].length" style="text-align: center">
                                Duration (months)
                            </th>
                        </tr>
                        <tr>
                            <th v-for="col in table[50].length">
                                <span v-if="col===1">0-3</span>
                                <span v-else-if="col===13">37+</span>
                                <span v-else>{{ col * 3 - 2 }}-{{ col * 3 }}</span>
                            </th>
                        </tr>
                        <tr v-for="(tr, age) in table">
                            <th>{{ age }}</th>
                            <td v-for="(td, index) in tr" :style="getTariffTableTdStyle(index, age)">
                                {{ currencyFormat(uplift ? Math.round(td*1.1) : td, '£', 0) }}
                            </td>
                        </tr>
                    </table>
                </div>
                <div v-if="loading.table">
                    <div :class="{ 'a-loader--table' : loading.table }"></div>
                </div>
                <p class="c-gd-psla-calculator__disclaimer">Data is up-to-date as of {{ currentRpi }} *</p>

                <h2>Awards</h2>
                <psla-award v-for="award in filteredAwards" :award="award" :showButtons="false" v-if="!loading.awards"></psla-award>
                <div v-if="loading.awards">
                    <div :class="{ 'a-loader--table' : loading.awards }"></div>
                </div>

            </div>
        </div>
    </mini-calculator-page>
</template>

<script type="text/babel">
    import {mapGetters, mapActions} from 'vuex';
    import MiniCalculatorPage from 'pages/mini-calculators/components/MiniCalculatorPageAlternativeWidth';
    import miniCalcIcons from 'components/Icons.vue';
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import FormValidator from 'components/form/validator/mixin';
    import DateInput from 'components/form/control/DateInput.vue';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import Intro from 'pages/mini-calculators/calculators/CalculatorComponents/Intro';
    import CalculatorKeyBindings from 'pages/mini-calculators/calculators/mixins/MiniCalculatorBindings';
    import roundValue from 'mixins/roundValue';
    import MiniCalculatorHeader from 'pages/mini-calculators/components/MiniCalculatorHeader';
    import currencyFormat from 'mixins/currencyFormat';
    import PslaAward from 'pages/fatal-accident-claims/dependency/steps/general-damages/PslaAward';

    export default {
        name: 'mesothelioma-psla-calculator',
        data() {
            return {
                loading: {
                    table: false,
                    awards: false
                },
                upliftButton: true,
                uplift: false,
                showResults: false,
                pslaAward: null
            }
        },
        computed: {
            ...mapGetters('mini/meso', ['value']),
            ...mapGetters('psla', {pslaValue: 'value'}),
            dateOfBirth: {
                get() {
                    return this.value('dateOfBirth');
                },
                set(value) {
                    this.updateDetail({key: 'dateOfBirth', value});

                    let mDob = moment(value, 'DD/MM/YYYY');
                    let mNow = moment();
                    let age = this.roundYears(mNow.diff(mDob, 'years', true));
                    this.updateDetail({key: 'ageOfPerson', value: age});
                }
            },
            durationOfSymptoms: {
                get() {
                    return this.value('durationOfSymptoms');
                },
                set(value) {
                    this.updateDetail({key: 'durationOfSymptoms', value});
                }
            },
            ageOfPerson: {
                get() {
                    return this.value('ageOfPerson');
                },
                set(value) {
                    this.updateDetail({key: 'ageOfPerson', value});

                    let mNow = moment();
                    let dob = mNow.subtract(value, 'years').format('DD/MM/YYYY');
                    this.updateDetail({key: 'dateOfBirth', value: dob});
                }
            },
            table: {
                get() {
                    return this.pslaValue('table');
                }
            },
            awards: {
                get() {
                    return this.pslaValue('awards');
                }
            },
            currentRpi: {
                get() {
                    return this.pslaValue('currentRpi');
                }
            },
            ages: {
                get() {
                    return Object.keys(this.table);
                }
            },
            upperAge: {
                get() {
                    let upperAge = this.ages.reverse().filter(age => age <= this.ageOfPerson);

                    if (!upperAge || upperAge.length < 1) {
                        return this.ages.reverse()[0];
                    }

                    return upperAge[0];
                }
            },
            filteredAwards() {
                if (!this.awards.length) {
                    return [];
                }
                return this.awards
                //Generate a score depending on how far they are away from our person
                    .map(award => {
                        let score = Math.abs(this.ageAtDeath - award.age) +
                            Math.abs(this.durationOfSymptoms - award.duration);
                        return {score, award};
                    })
                    // Sort into score order
                    .sort((a, b) => {
                        if (a.score === b.score) {
                            return 0;
                        }
                        return a.score - b.score;
                    })
                    // Return just the awards
                    .map(info => info.award)
                    // Top 10 awards
                    .slice(0, 10);
            },
        },
        mixins: [
            FormValidator,
            CalculatorKeyBindings,
            roundValue,
            currencyFormat
        ],
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        components: {
            Control,
            ControlError,
            DateInput,
            Intro,
            MiniCalculatorPage,
            MiniCalculatorHeader,
            miniCalcIcons,
            PslaAward
        },
        created() {
            this.resetForm();
            this.loadTableCheck();
            this.loadAwardsCheck();
            this.loadCurrentRpi();
        },
        methods: {
            ...mapActions('mini/meso', {
                calculateAge: 'calculateAge',
                calculateDateOfBirth: 'calculateDateOfBirth',
                updateDetail: 'update',
                resetForm: 'reset'
            }),
            ...mapActions('psla', ['loadTable', 'loadAwards', 'loadCurrentRpi']),
            loadTableCheck() {
                if (!this.table.length) {
                    this.loading.table = true;
                    this.loadTable().then(() => {
                        this.loading.table = false;
                    });
                }
            },
            loadAwardsCheck() {
                this.awards = [];
                this.loading.awards = true;
                this.loadAwards().then(() => {
                    this.loading.awards = false;
                });
            },
            isExactMatch(index, age) {
                return (this.isColMatch(index) && this.isRowMatch(age));
            },
            isColMatch(index) {
                index++;

                if (index === 1 && this.durationOfSymptoms <= 3) {
                    return true;
                }

                if (index === 13 && this.durationOfSymptoms >= 37) {
                    return true;
                }

                return (this.durationOfSymptoms >= index * 3 - 2 && this.durationOfSymptoms <= index * 3);
            },
            isRowMatch(age) {
                return (this.upperAge === age);
            },
            getTariffTableTdStyle(index, age) {
                if (this.isExactMatch(index, age)) {
                    this.setPslaAward(this.table[age][index]);
                    return 'cursor: pointer; background:#c5ecfe; font-weight: bold; font-size: 14px;';
                }

                if (this.isColMatch(index) || this.isRowMatch(age)) {
                    return 'cursor: pointer; background:#c5ecfe; font-size: 14px;';
                }

                return 'cursor: pointer; font-size: 14px;';
            },
            applyUplift() {
                this.upliftButton = !this.upliftButton;
                this.uplift = !this.uplift;
            },
            isValid() {
                return (
                    this.durationOfSymptoms !== null
                    && this.durationOfSymptoms !== ""
                    && this.dateOfBirth !== null
                    && this.dateOfBirth !== ""
                    && this.ageOfPerson !== null
                    && this.ageOfPerson !== ""
                    && moment(this.dateOfBirth, 'DD/MM/YYYY').isValid()
                );
            },
            roundYears(years) {
                return Math.round(years * 100) / 100;
            },
            setPslaAward(value) {
                if (this.showResults) {
                    this.uplift ? this.pslaAward = (value * 1.1) : this.pslaAward = value;
                }
            },
            handler() {
                if (!this.isValid()) {
                    this.showPrompt();
                    this.pslaAward = null;
                    return;
                }

                this.loadTableCheck();
                this.loadAwardsCheck();
                this.loadCurrentRpi();

                this.hidePrompt();
                this.showResults = true;
            }
        },
    }
</script>



// WEBPACK FOOTER //
// mesoCalculator.vue?c5ac900a